/**
 * RootErrorBoundary — top-level error boundary wrapping <App/>.
 *
 * Catches any render or lifecycle error in the entire app and renders a
 * recovery screen instead of a blank page. Three escape hatches are
 * offered, in order of severity:
 *   1. Try again (resets state, re-renders without reload)
 *   2. Reload page (full hard reload — picks up new deploys)
 *   3. Reset app data (clears fornland-* localStorage keys and reloads —
 *      last-resort for corrupted cache)
 *
 * The error message + stack is persisted to localStorage under
 * `fornland-last-error` so the next session can offer to report it (or
 * we can inspect it during a support back-and-forth).
 *
 * Localised reads `fornland-lang` from localStorage so the recovery
 * screen speaks the user's UI language; falls back to Danish.
 *
 * Mounted from main.tsx, replacing the previous inline ErrorBoundary.
 */
import React, { Component } from "react";
import type { ReactNode, ErrorInfo } from "react";

type Lang = "da" | "no" | "sv" | "en" | "fi" | "is" | "kl" | "de";

const COPY: Record<Lang, {
  title: string;
  description: string;
  retry: string;
  reload: string;
  reset: string;
  resetConfirm: string;
  showDetails: string;
  hideDetails: string;
  offline: string;
}> = {
  da: {
    title: "Noget gik galt",
    description: "Fornland kunne ikke gengive denne side. Prøv en af mulighederne herunder.",
    retry: "Prøv igen",
    reload: "Genindlæs siden",
    reset: "Nulstil og genindlæs",
    resetConfirm: "Det sletter gemte filtre, besøg, samlinger og cache. Fortsæt?",
    showDetails: "Vis tekniske detaljer",
    hideDetails: "Skjul detaljer",
    offline: "Du er offline. Tjek din internetforbindelse.",
  },
  no: {
    title: "Noe gikk galt",
    description: "Fornland kunne ikke vise denne siden. Prøv ett av alternativene nedenfor.",
    retry: "Prøv igjen", reload: "Last siden på nytt", reset: "Nullstill og last på nytt",
    resetConfirm: "Dette sletter lagrede filtre, besøk, samlinger og cache. Fortsette?",
    showDetails: "Vis tekniske detaljer", hideDetails: "Skjul detaljer",
    offline: "Du er frakoblet. Sjekk Internett-tilkoblingen.",
  },
  sv: {
    title: "Något gick fel",
    description: "Fornland kunde inte visa denna sida. Försök ett av alternativen nedan.",
    retry: "Försök igen", reload: "Ladda om sidan", reset: "Nollställ och ladda om",
    resetConfirm: "Detta tar bort sparade filter, besök, samlingar och cache. Fortsätta?",
    showDetails: "Visa tekniska detaljer", hideDetails: "Dölj detaljer",
    offline: "Du är offline. Kontrollera din internetanslutning.",
  },
  en: {
    title: "Something went wrong",
    description: "Fornland couldn't render this page. Try one of the options below.",
    retry: "Try again", reload: "Reload page", reset: "Reset and reload",
    resetConfirm: "This clears saved filters, visits, collections and cache. Continue?",
    showDetails: "Show technical details", hideDetails: "Hide details",
    offline: "You're offline. Check your internet connection.",
  },
  fi: {
    title: "Jokin meni pieleen",
    description: "Fornland ei voinut näyttää tätä sivua. Kokeile alla olevia vaihtoehtoja.",
    retry: "Yritä uudelleen", reload: "Lataa sivu uudelleen", reset: "Nollaa ja lataa uudelleen",
    resetConfirm: "Tämä poistaa tallennetut suodattimet, käynnit, kokoelmat ja välimuistin. Jatketaanko?",
    showDetails: "Näytä tekniset tiedot", hideDetails: "Piilota tiedot",
    offline: "Olet offline-tilassa. Tarkista internetyhteytesi.",
  },
  is: {
    title: "Eitthvað fór úrskeiðis",
    description: "Fornland gat ekki birt þessa síðu. Prófaðu einn af valkostunum hér að neðan.",
    retry: "Reyna aftur", reload: "Endurhlaða síðu", reset: "Endurstilla og endurhlaða",
    resetConfirm: "Þetta eyðir vistuðum síum, heimsóknum, söfnum og skyndiminni. Halda áfram?",
    showDetails: "Sýna tæknilegar upplýsingar", hideDetails: "Fela upplýsingar",
    offline: "Þú ert án nettengingar. Athugaðu nettenginguna.",
  },
  de: {
    title: "Etwas ist schiefgegangen",
    description: "Fornland konnte diese Seite nicht anzeigen. Probier eine der Möglichkeiten hier unten.",
    retry: "Noch einmal versuchen", reload: "Seite neu laden", reset: "Zurücksetzen und neu laden",
    resetConfirm: "Damit werden gespeicherte Filter, Besuche, Sammlungen und der Zwischenspeicher gelöscht. Weiter?",
    showDetails: "Technische Details zeigen", hideDetails: "Details ausblenden",
    offline: "Du bist offline. Prüf deine Internetverbindung.",
  },
  kl: {
    title: "Sumiifik ajortuusoq",
    description: "Fornland-imi quppernermut takuneqarsinnaanngilaq. Periarfissat ataani misilikkit.",
    retry: "Misiligaqqikkiuk", reload: "Quppernermik nutaarsigaqqikkiuk", reset: "Salinneqassaaq aamma nutaarsigaqqissoq",
    resetConfirm: "Tassanngaanniit isumagisat, ornigassat, katersugaasiviit aamma katersugaaqarfiit nungunneqarput. Ingerlatigukkiuk?",
    showDetails: "Inissinneqarsimasut takutiguk", hideDetails: "Inissinneqarsimasut isertiguk",
    offline: "Atassuteqarfiup ataani inissisimavutit. Internetimi atorfissaqartitsiviit kisitsikkit.",
  },
};

function detectLang(): Lang {
  try {
    const stored = localStorage.getItem("fornland-lang");
    if (stored && stored in COPY) return stored as Lang;
  } catch { /* localStorage may throw in private-mode iframes */ }
  // Fall back to navigator.language first chunk.
  const nav = (typeof navigator !== "undefined" ? navigator.language : "").slice(0, 2).toLowerCase();
  if (nav in COPY) return nav as Lang;
  return "da";
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

export class RootErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, errorInfo: null, showDetails: false };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ errorInfo: info });
    console.error("[Fornland] App crash:", error, info);
    // Persist so it survives the reload and can be inspected next session.
    try {
      localStorage.setItem("fornland-last-error", JSON.stringify({
        ts: new Date().toISOString(),
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        url: typeof location !== "undefined" ? location.href : "",
      }));
    } catch { /* quota or private-mode failure — ignore */ }
  }

  private retry = () => this.setState({ error: null, errorInfo: null, showDetails: false });

  private hardReload = () => {
    try { location.reload(); } catch { /* shouldn't fail */ }
  };

  private resetAndReload = () => {
    const lang = detectLang();
    if (!window.confirm(COPY[lang].resetConfirm)) return;
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("fornland-")) keysToRemove.push(k);
      }
      for (const k of keysToRemove) localStorage.removeItem(k);
    } catch { /* ignore */ }
    location.reload();
  };

  private toggleDetails = () => this.setState((s) => ({ showDetails: !s.showDetails }));

  render() {
    if (!this.state.error) return this.props.children;

    const lang = detectLang();
    const copy = COPY[lang];
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;

    const btn: React.CSSProperties = {
      padding: "12px 24px", minHeight: 44, borderRadius: "var(--radius-lg)", border: "1px solid rgba(255,255,255,0.12)",
      background: "transparent", color: "var(--text-secondary, #ccc)", fontSize: 14, fontWeight: 600,
      cursor: "pointer", fontFamily: "inherit", letterSpacing: 0.2, transition: "all 0.15s ease",
    };
    const primaryBtn: React.CSSProperties = {
      ...btn, background: "var(--btn-primary-bg, #fff)", color: "var(--btn-primary-text, #0a0a0a)",
      borderColor: "transparent", fontWeight: 700,
    };
    const dangerBtn: React.CSSProperties = {
      ...btn, color: "rgba(248,113,113,0.85)", borderColor: "rgba(248,113,113,0.25)",
    };

    return (
      <div
        role="alert"
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          minHeight: "100dvh", padding: 32, fontFamily: "'Inter', system-ui, sans-serif",
          background: "var(--bg-base, #0a0a0a)", color: "var(--text-primary, #fff)", textAlign: "center",
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: 6, marginBottom: 12, textTransform: "uppercase" }}>FORNLAND</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: "12px 0 8px" }}>{copy.title}</h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary, #999)", maxWidth: 380, lineHeight: 1.6, margin: "0 0 24px" }}>
          {offline ? copy.offline : copy.description}
        </p>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", maxWidth: 480 }}>
          <button onClick={this.retry} style={primaryBtn}>{copy.retry}</button>
          <button onClick={this.hardReload} style={btn}>{copy.reload}</button>
          <button onClick={this.resetAndReload} style={dangerBtn}>{copy.reset}</button>
        </div>

        <button
          onClick={this.toggleDetails}
          style={{
            marginTop: 32, background: "none", border: "none", color: "var(--text-faint, #888)",
            fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline",
            textDecorationStyle: "dotted", textUnderlineOffset: 4,
          }}
        >
          {this.state.showDetails ? copy.hideDetails : copy.showDetails}
        </button>

        {this.state.showDetails && (
          <pre style={{
            marginTop: 16, padding: 16, borderRadius: "var(--radius-sm)", background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)", maxWidth: "min(720px, 92vw)", maxHeight: 280,
            overflow: "auto", textAlign: "left", fontSize: 11, fontFamily: "ui-monospace, SFMono-Regular, monospace",
            color: "var(--text-tertiary, #aaa)", lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word",
          }}>
            {this.state.error.name}: {this.state.error.message}
            {this.state.error.stack ? "\n\n" + this.state.error.stack : ""}
            {this.state.errorInfo?.componentStack ? "\n--- component stack ---" + this.state.errorInfo.componentStack : ""}
          </pre>
        )}
      </div>
    );
  }
}
